import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Activity, KeyRound } from "lucide-react";

export default function ChangePassword() {
  const { user, loading, changePassword, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="text-sm text-zinc-500">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;

  async function onSubmit(e) {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (next !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      await changePassword(current, next);
      toast.success("Password updated");
      navigate("/", { replace: true });
    } catch (e) {
      toast.error("Could not change password", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSubmitting(false);
    }
  }

  const forced = user.must_change_password;

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6" data-testid="change-password-page">
      <Card className="w-full max-w-md p-8 border border-zinc-200 shadow-sm">
        <div className="flex items-center gap-2.5 mb-6">
          <div className="h-9 w-9 grid place-items-center bg-zinc-950 text-white rounded-md">
            <Activity className="h-4 w-4" strokeWidth={2.25} />
          </div>
          <div className="font-display text-xl font-bold tracking-tight text-zinc-950">
            Acuity<span className="text-zinc-400">/</span>Reports
          </div>
        </div>

        <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-950 flex items-center gap-2">
          <KeyRound className="h-5 w-5" /> {forced ? "Set a new password" : "Change password"}
        </h1>
        {forced ? (
          <p className="mt-1 text-sm text-amber-700">
            For security, you must set a new password before continuing.
          </p>
        ) : (
          <p className="mt-1 text-sm text-zinc-600">
            Logged in as <span className="font-mono-num">{user.email}</span>
          </p>
        )}

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="cp-current">Current password</Label>
            <Input
              id="cp-current"
              data-testid="cp-current-input"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="mt-1.5 bg-white"
            />
          </div>
          <div>
            <Label htmlFor="cp-new">New password</Label>
            <Input
              id="cp-new"
              data-testid="cp-new-input"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="mt-1.5 bg-white"
            />
            <p className="mt-1.5 text-xs text-zinc-500">At least 8 characters.</p>
          </div>
          <div>
            <Label htmlFor="cp-confirm">Confirm new password</Label>
            <Input
              id="cp-confirm"
              data-testid="cp-confirm-input"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-1.5 bg-white"
            />
          </div>

          <div className="flex gap-2 pt-1">
            <Button
              type="submit"
              disabled={submitting}
              data-testid="cp-submit-button"
              className="flex-1 bg-zinc-950 hover:bg-zinc-800 text-white"
            >
              {submitting ? "Updating…" : "Update password"}
            </Button>
            {!forced && (
              <Button
                type="button"
                variant="outline"
                data-testid="cp-cancel-button"
                onClick={() => navigate(-1)}
                className="border-zinc-300"
              >
                Cancel
              </Button>
            )}
          </div>
          {forced && (
            <button
              type="button"
              onClick={() => logout().then(() => navigate("/login", { replace: true }))}
              data-testid="cp-signout-link"
              className="block mx-auto mt-2 text-xs text-zinc-500 hover:text-zinc-900 underline-offset-2 hover:underline"
            >
              Sign out instead
            </button>
          )}
        </form>
      </Card>
    </div>
  );
}
